import { Show, JSX, Accessor } from 'solid-js';
import clsx from 'clsx';

interface FieldErrorMessageProps {
    errors: Accessor<Record<string, string | undefined>>;
    fieldKey: string | Accessor<string>;
    id?: string;
}

interface FormStylingProps {
    children?: JSX.Element;
}

interface FormFieldLabelProps {
    children?: JSX.Element;
    for?: string;
    id?: string;
}

interface FormSelectProps {
    children?: JSX.Element;
    value: string;
    hasErrors?: boolean;
    id?: string;
    required?: boolean;
    onChange?: JSX.EventHandlerUnion<HTMLSelectElement, Event>;
    name?: string;
    'aria-labelledby'?: string;
    'aria-describedby'?: string;
    title?: string;
    autocomplete?: string;
}

interface FormInputProps {
    id?: string;
    hasErrors?: boolean;
    required?: boolean;
    value?: string;
    onChange?: JSX.EventHandlerUnion<HTMLInputElement, Event>;
    placeholder?: string;
    name?: string;
    'aria-label'?: string;
    'aria-describedby'?: string;
    title?: string;
    autocomplete?: string;
    type?: string;
}

interface FormTextareaProps {
    id?: string;
    hasErrors?: boolean;
    required?: boolean;
    value?: string;
    onChange?: JSX.EventHandlerUnion<HTMLTextAreaElement, Event>;
    placeholder?: string;
    name?: string;
    rows?: number;
    'aria-label'?: string;
    'aria-describedby'?: string;
    title?: string;
}

/* DS form-control sizing comes from `--spacing-cvi-field-{x,y}` (13px/11px) defined in
   tailwind-theme-misc.css. Off the cvi-N scale because the values are component-specific. */
export const fieldShellClasses = (hasErrors?: boolean) => clsx(
    'w-full bg-cvi-bg-field rounded-cvi-md border px-cvi-field-x py-cvi-field-y',
    'label-md font-cvi-regular text-cvi-fg-default placeholder:text-cvi-fg-muted',
    'focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-offset-cvi-bg-body',
    hasErrors
        ? 'border-cvi-border-danger focus:border-cvi-border-danger focus:ring-cvi-focus-danger'
        : 'border-cvi-border-strong focus:border-cvi-border-interactive focus:ring-cvi-focus-default',
);

// Id of a field's error message, and the aria-describedby value that points at it while the error shows.
export const fieldErrorId = (fieldId: string) => `${fieldId}-error`;
export const errorDescribedBy = (fieldId: string, hasError: boolean) => (hasError ? fieldErrorId(fieldId) : undefined);

export const FieldErrorMessage = (props: FieldErrorMessageProps): JSX.Element => {
    const fieldKey = () => typeof props.fieldKey === 'function'
        ? props.fieldKey()
        : props.fieldKey;

    return (
        <Show when={props.errors()[fieldKey()]}>
            <div
                id={props.id}
                class="pt-cvi-3 body-sm font-cvi-regular text-cvi-fg-danger"
                innerHTML={props.errors()[fieldKey()]!}
            />
        </Show>
    );
};

export const IconWrapper = (props: FormStylingProps) => {
    return (
        <div class="absolute right-cvi-5 inset-y-0 flex items-center pointer-events-none text-cvi-fg-muted">
            {props.children}
        </div>
    );
};

export const FormSelectWrapper = (props: FormStylingProps) => {
    return (
        <div class="relative">{props.children}</div>
    );
};

export const FormFieldLabel = (props: FormFieldLabelProps) => {
    return (
        <label
            for={props.for}
            id={props.id}
            class="block pb-cvi-3 label-sm font-cvi-regular text-cvi-fg-muted"
        >
            {props.children}
        </label>
    );
};

export const FormSelect = (props: FormSelectProps) => {
    return (
        <select
            id={props.id}
            required={props.required}
            value={props.value || ''}
            onChange={props.onChange}
            name={props.name}
            aria-labelledby={props['aria-labelledby']}
            aria-describedby={props['aria-describedby']}
            title={props.title}
            autocomplete={props.autocomplete}
            aria-invalid={props.hasErrors || undefined}
            class={clsx(
                fieldShellClasses(props.hasErrors),
                'appearance-none cursor-pointer pr-cvi-9',
            )}
        >
            {props.children}
        </select>
    );
};

export const FormInput = (props: FormInputProps) => {
    return (
        <input
            id={props.id}
            type={props.type || 'text'}
            required={props.required}
            value={props.value || ''}
            onChange={props.onChange}
            placeholder={props.placeholder}
            name={props.name}
            aria-label={props['aria-label']}
            aria-describedby={props['aria-describedby']}
            title={props.title}
            autocomplete={props.autocomplete}
            aria-invalid={props.hasErrors || undefined}
            class={fieldShellClasses(props.hasErrors)}
        />
    );
};

export const FormTextarea = (props: FormTextareaProps) => {
    return (
        <textarea
            id={props.id}
            required={props.required}
            value={props.value || ''}
            onChange={props.onChange}
            placeholder={props.placeholder}
            name={props.name}
            rows={props.rows}
            aria-label={props['aria-label']}
            aria-describedby={props['aria-describedby']}
            title={props.title}
            aria-invalid={props.hasErrors || undefined}
            class={clsx(
                fieldShellClasses(props.hasErrors),
                'min-h-cvi-textarea-min-h resize-y',
            )}
        />
    );
};
